import {CanActivateFn, Router, UrlTree} from "@angular/router";
import {inject} from "@angular/core";
import {catchError, map, switchMap, take} from "rxjs/operators";
import {from, of} from "rxjs";
import {Auth, IdTokenResult, user} from "@angular/fire/auth";
import {environment} from "../../environments/environment";
import {EhrClaims} from "../_interfaces/ehr-claims.interface";

/**
 * Guard for the 2FA verification step.
 *
 * Rules:
 * - A Firebase user must already exist (login step completed).
 * - If no user/token is present -> redirect to /login.
 * - If user exists -> allow access to verify-code route.
 */
export const verifyGuard: CanActivateFn = (_route, state) => {
  const auth = inject(Auth);
  const router = inject(Router);

  const toLogin = (): UrlTree =>
    router.createUrlTree(["login"], {
      queryParams: {redirectURL: state.url},
    });

  const user$ = user(auth); // Observable<User | null>

  return user$.pipe(
    take(1),

    // Fetch the ID token if the user exists
    switchMap(u => {
      if (!u) return of<IdTokenResult | null>(null);
      return from(u.getIdTokenResult(true));
    }),

    map(token => {
      // No token/user means the user shouldn't be on the verify route
      if (!token) return toLogin();

      const claims = (token.claims ?? {}) as EhrClaims;

      if (!environment.production) {
        console.log("VerifyGuard claims:", claims);
      }

      // Allow access to verify-code (user logged in but no Office session yet)
      return true;
    }),

    catchError(err => {
      if (!environment.production) {
        console.error("VerifyGuard error:", err);
      }
      // Recover by forcing the login flow to restart
      return of(toLogin());
    })
  );
};
